import { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { UserPlus, Send, Check, Clock } from "lucide-react";

import TopBar from "../../Components/AddRelationshipTopBar";
import ContentLoader from "../LoadingPage/ContentLoader";
import amplitudeService from "../../Services/amplitude";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { showSuccessToast, showErrorToast } from "../../Utils/toast";
import {
  getShareArisGrants,
  sendShareArisInvitation,
  cancelShareArisGrant,
  splitGrants,
} from "../../Services/shareAris";
// Reuse the Connections page + Add Connection form styles for visual consistency
// (blue buttons, left-aligned fields, .grid-2/.field/.actions). Our own classes
// use a `sa-` prefix to avoid collisions with those files (e.g. .share-card).
import "../ManageRelationship/style.css";
import "../../Components/AddRelationshipForm/style.css";
import "./style.css";

const PASS_CAP = 3;
const OCCUPYING = ["invitation_sent", "accepted_incomplete", "active", "renewal_off"];
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

const STATUS_PILL = {
  invitation_sent: { label: "Invitation sent", cls: "pending", icon: Clock },
  accepted_incomplete: { label: "Registration not complete", cls: "pending", icon: Clock },
  active: { label: "Active", cls: "accepted", icon: Check },
  renewal_off: { label: "Renewal off", cls: "pending", icon: Clock },
  upgraded: { label: "Upgraded to Full Membership", cls: "accepted", icon: Check },
  expired: { label: "Completed", cls: "", icon: null },
};

function GrantRow({ grant, onCancel }) {
  const pill = STATUS_PILL[grant.status] || { label: grant.status, cls: "", icon: null };
  const PillIcon = pill.icon;
  const name = [grant.invited_first_name, grant.invited_last_name].filter(Boolean).join(" ") || "Invited person";
  const canCancel = ["invitation_sent", "accepted_incomplete"].includes(grant.status);
  return (
    <div className="sa-row">
      <div className="sa-row-info">
        <span className="sa-row-name">{name}</span>
        {pill.label && (
          <span className={`sa-pill ${pill.cls}`}>
            {PillIcon && <PillIcon size={13} />} {pill.label}
          </span>
        )}
      </div>
      {canCancel && (
        <button className="sa-row-cancel" onClick={() => onCancel(grant)}>Cancel Invitation</button>
      )}
    </div>
  );
}

export default function ShareAris() {
  const { setHeaderContent } = useHeader();
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  const [view, setView] = useState("home"); // home | invite
  const [benefitType, setBenefitType] = useState("companion");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [channel, setChannel] = useState("email"); // email | phone
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [connect, setConnect] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const { grants } = await getShareArisGrants();
    setGrants(grants || []);
    setLoading(false);
  };
  useEffect(() => {
    setHeaderContent(false);
    load();
    try { amplitudeService.track("share_aris_opened"); } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { companion, passes } = useMemo(() => splitGrants(grants), [grants]);
  const passesUsed = passes.filter((p) => OCCUPYING.includes(p.status)).length;
  const passesAvailable = Math.max(0, PASS_CAP - passesUsed);
  const activePasses = passes.filter((p) => OCCUPYING.includes(p.status));

  const openInvite = (type) => {
    setBenefitType(type);
    setFirstName(""); setLastName(""); setChannel("email"); setEmail(""); setPhone(""); setConnect(true);
    setView("invite");
    try {
      amplitudeService.track(type === "companion" ? "companion_invite_started" : "pass_invite_started");
    } catch (_) {}
  };

  const canSubmit = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (channel === "email") return isValidEmail(email);
    return Boolean(phone && parsePhoneNumberFromString(phone)?.isValid());
  }, [firstName, lastName, channel, email, phone]);

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    const payload = {
      benefit_type: benefitType,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      connect_on_activation: connect,
    };
    if (channel === "email") payload.invited_email = email.trim();
    else payload.invited_phone = parsePhoneNumberFromString(phone)?.nationalNumber || phone;

    const { grant, error } = await sendShareArisInvitation(payload);
    setSubmitting(false);
    if (error) {
      if (error.status === 403) showErrorToast("Your membership does not include Share Aris.");
      else if (error.status === 409) showErrorToast("You have no benefits available right now.");
      return;
    }
    if (grant) {
      showSuccessToast("Invitation sent");
      setView("home");
      load();
    }
  };

  const onCancel = async (grant) => {
    const { error } = await cancelShareArisGrant(grant.id || grant._id);
    if (!error) { showSuccessToast("Invitation cancelled"); load(); }
  };

  const isCompanion = benefitType === "companion";

  return (
    <div className="manage-container share-aris-page">
      <TopBar
        title="Share Aris"
        showBackButton
        onBackClick={() => (view === "invite" ? setView("home") : window.history.back())}
      />

      {loading ? (
        <div className="manage-loading-container"><ContentLoader /></div>
      ) : view === "invite" ? (
        <div className="manage-wrapper">
          <div className="addrel-card">
            <div className="sa-form-head">
              <h2 className="relationships-title">
                {isCompanion ? "Invite Your Companion" : "Send an Aris Pass"}
              </h2>
              <p className="sa-form-sub">
                {isCompanion
                  ? "Choose one person you would like to bring into Aris."
                  : "Invite someone to experience Aris for 120 days."}
              </p>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>First Name</label>
                <input value={firstName} placeholder="First Name" onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input value={lastName} placeholder="Last Name" onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label>Send invitation by</label>
              <div className="sa-radio-row">
                <label className="sa-radio">
                  <input type="radio" checked={channel === "email"} onChange={() => setChannel("email")} /> Email
                </label>
                <label className="sa-radio">
                  <input type="radio" checked={channel === "phone"} onChange={() => setChannel("phone")} /> Text message
                </label>
              </div>
              {channel === "email" ? (
                <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
              ) : (
                <PhoneInput international defaultCountry="US" value={phone} onChange={(v) => setPhone(v || "")} className="phone-input" />
              )}
            </div>

            <label className="sa-toggle-row">
              <span>Connect with this person on Aris</span>
              <input type="checkbox" checked={connect} onChange={(e) => setConnect(e.target.checked)} />
            </label>
            <p className="sa-form-hint">
              Being connected allows you to start Aris Dialogues for Two. Neither person can see the other's private conversations or activity.
            </p>

            {isCompanion && (
              <p className="sa-notice">
                <strong>Before you send:</strong> Once this person activates the Companion Membership, it is assigned to them through the next annual renewal date. You may stop the next renewal, but you cannot reassign it during the active year.
              </p>
            )}

            <div className="actions">
              <button className="primary" disabled={!canSubmit || submitting} onClick={submit}>
                <Send size={16} /> {submitting ? "Sending…" : (isCompanion ? "Send Invitation" : "Send Aris Pass")}
              </button>
              <button className="ghost" onClick={() => setView("home")}>Cancel</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="manage-wrapper">
          <p className="sa-intro">
            Bring the people who matter to you into Aris. Your membership includes one Companion Membership and three Aris Passes. Each person receives their own private Aris account.
          </p>

          <section className="sa-section">
            <div className="sa-section-head">
              <h3 className="sa-section-title">Your Companion Membership</h3>
              {!companion && (
                <button className="add-relationship-btn sa-cta" onClick={() => openInvite("companion")}>
                  <UserPlus size={16} /> Invite Your Companion
                </button>
              )}
            </div>
            {companion ? (
              <div className="sa-list"><GrantRow grant={companion} onCancel={onCancel} /></div>
            ) : (
              <p className="sa-help">Invite one person to join you on Aris — their own renewable membership with text access and five voice minutes.</p>
            )}
          </section>

          <section className="sa-section">
            <div className="sa-section-head">
              <div>
                <h3 className="sa-section-title">Your Aris Passes</h3>
                <p className="sa-help">{passesAvailable} of {PASS_CAP} available · give someone 120 days to experience Aris.</p>
              </div>
              <button className="add-relationship-btn sa-cta" disabled={passesAvailable === 0} onClick={() => openInvite("pass")}>
                <Send size={16} /> Send an Aris Pass
              </button>
            </div>
            {activePasses.length > 0 && (
              <div className="sa-list">
                {activePasses.map((p) => <GrantRow key={p.id || p._id} grant={p} onCancel={onCancel} />)}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
